import * as React from "react";
import YouTube from "react-youtube";
import { ChevronUp, ChevronDown, Volume2, VolumeX, Gamepad2, Play, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const REELS_REFRESH_INTERVAL = 3 * 60 * 1000;

export const GameReels = () => {
  const [reels, setReels] = React.useState([]);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isMuted, setIsMuted] = React.useState(true);
  const [loading, setLoading] = React.useState(true);

  const containerRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const touchStartY = React.useRef(0);
  const lastScrollTime = React.useRef(0);
  const userWantsSound = React.useRef(false);

  // Fetch reels
  const fetchReels = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reels?_t=${Date.now()}`);
      const data = await response.json();
      if (data.reels?.length > 0) {
        setReels(data.reels);
      }
    } catch (err) {
      console.error("Error fetching reels:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchReels(); }, []);
  React.useEffect(() => {
    const interval = setInterval(fetchReels, REELS_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchReels]);

  const currentReel = reels[currentIndex];

  // Navigation
  const goNext = React.useCallback(() => {
    if (reels.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % reels.length);
  }, [reels.length]);

  const goPrev = React.useCallback(() => {
    if (reels.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + reels.length) % reels.length);
  }, [reels.length]);

  // No manual loadVideoById needed - react-youtube handles videoId prop changes

  // Toggle mute
  const toggleMute = React.useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    try {
      if (isMuted) {
        player.unMute();
        player.setVolume(100);
        userWantsSound.current = true;
        setIsMuted(false);
      } else {
        player.mute();
        userWantsSound.current = false;
        setIsMuted(true);
      }
    } catch (e) {
      console.error("Error toggling mute:", e);
    }
  }, [isMuted]);

  // Player events
  const onPlayerReady = React.useCallback((event) => {
    playerRef.current = event.target;
    event.target.playVideo();
    // Restore sound preference
    if (userWantsSound.current) {
      event.target.unMute();
      event.target.setVolume(100);
      setIsMuted(false);
    }
  }, []);

  const onPlayerStateChange = React.useCallback((event) => {
    // 1 = PLAYING - sync mute state
    if (event.data === 1) {
      try {
        const actuallyMuted = event.target.isMuted();
        if (userWantsSound.current && actuallyMuted) {
          event.target.unMute();
          event.target.setVolume(100);
          setIsMuted(false);
        }
      } catch (e) { /* ignore */ }
    }
    // 0 = ENDED - go to next reel
    if (event.data === 0) {
      goNext();
    }
  }, [goNext]);

  // Scroll handling - prevent page scroll
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const now = Date.now();
      if (now - lastScrollTime.current < 600) return;
      lastScrollTime.current = now;
      if (e.deltaY > 0) goNext();
      else goPrev();
    };

    const preventScroll = (e) => { e.preventDefault(); };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', preventScroll);
    };
  }, [goNext, goPrev]);

  // Touch swipe
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  // YouTube player opts - muted autoplay for browser compliance
  const opts = React.useMemo(() => ({
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 1,
      mute: 1,
      controls: 0,
      showinfo: 0,
      rel: 0,
      modestbranding: 1,
      playsinline: 1,
      origin: window.location.origin,
      iv_load_policy: 3,
      fs: 0,
      disablekb: 1,
    },
  }), []);

  if (loading) {
    return (
      <Card className="h-full border-white/10 bg-white/5 flex items-center justify-center">
        <div className="text-center p-4">
          <div className="size-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        </div>
      </Card>
    );
  }

  if (reels.length === 0) {
    return (
      <Card className="h-full border-white/10 bg-white/5 flex items-center justify-center">
        <div className="text-center p-4">
          <Gamepad2 className="size-10 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Trailer bulunamadi</p>
        </div>
      </Card>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full rounded-2xl overflow-hidden border border-white/10 bg-black touch-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="game-reels-container"
    >
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 right-0 z-20 h-1 bg-white/20">
        <div
          className="h-full bg-gradient-to-r from-primary to-purple-400 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / reels.length) * 100}%` }}
        />
      </div>

      {/* YouTube Player */}
      <div className="relative w-full h-full">
        <YouTube
          key={currentReel?.videoId}
          videoId={currentReel?.videoId}
          opts={opts}
          onReady={onPlayerReady}
          onStateChange={onPlayerStateChange}
          className="absolute inset-0 w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
          iframeClassName="w-full h-full"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none z-[1]" />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          <Badge className="bg-red-500/90 text-white border-0 gap-1" data-testid="reels-badge">
            <Play className="size-3" />
            Reels
          </Badge>
          <Badge className="bg-green-600/90 text-white border-0 gap-1 text-[10px]">
            <Shield className="size-2.5" />
            Resmi Fragman
          </Badge>
        </div>

        {/* Mute Button */}
        <button
          onClick={toggleMute}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          data-testid="reels-mute-btn"
        >
          {isMuted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
        </button>

        {/* Navigation Arrows */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
          <button
            onClick={goPrev}
            className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            data-testid="reels-prev-btn"
          >
            <ChevronUp className="size-5" />
          </button>
          <button
            onClick={goNext}
            className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            data-testid="reels-next-btn"
          >
            <ChevronDown className="size-5" />
          </button>
        </div>

      </div>
    </div>
  );
};
